import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Field,
  FieldProps,
  useField,
} from 'formik';
import TextField from 'hellospa/foundation/control/text-field';
import ErrorMessage from 'hellospa/components/error-message';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import ContactInput from 'hellospa/components/contact-input';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import HelpIcon from './icons/help';
import RemoveIcon from './icons/close';
import TooltipUI, {
  ArrowDirection,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';
import * as types from 'hellospa/page/prep-and-send/data/types';
import styles from './index.scss';
import messages from './intl';
import classNames from 'classnames';

type AuthenticationTypes = 'accessCode' | 'smsAuthNumber'

type Props = {
  index: number,
  recipient: types.Signer | types.Recipient,
  onClose: () => void,
}

interface InitialValues {
  authType: AuthenticationTypes;
}

const AuthenticationFields = (props: Props) => {
  const intl = useIntl();
  const parent = `recipients.${props.index}`;
  const accessCodeFieldName = `${parent}.accessCode`;
  const smsAuthFieldName = `${parent}.smsAuthNumber`;
  const [, accessCodeMeta, accessCodeHelpers] = useField<String>(accessCodeFieldName);
  const [, smsAuthNumberMeta, smsAuthNumberHelpers] = useField<String>(smsAuthFieldName);
  const [, authTypeMeta, authTypeHelpers] = useField<InitialValues['authType']>(`${parent}.authType`);

  const allowRecipientSmsAuth = useSelector(selectors.allowRecipientSmsAuth);
  const allowRecipientAccessCode = useSelector(selectors.allowRecipientAccessCode);
  const canAccessRecipientAccessCodes = useSelector(selectors.canAccessRecipientAccessCodes);

  const isDefaultOrAccessCode = (allowRecipientAccessCode || canAccessRecipientAccessCodes)
    && (!authTypeMeta.value || authTypeMeta.value === 'accessCode');

  const isSmsAuth = allowRecipientSmsAuth && (authTypeMeta.value === 'smsAuthNumber' || !isDefaultOrAccessCode);

  const dropdownButtonText = isDefaultOrAccessCode
    ? intl.formatMessage(messages.accessCodeLabel)
    : intl.formatMessage(messages.recipientAuthenticationDropdownSmsAuth);

  // Display text input depending on which authentication method is selected
  const handleSelection = React.useCallback((
    selection: AuthenticationTypes,
  ) => {
    authTypeHelpers.setValue(selection);
  }, [authTypeHelpers]);

  React.useEffect(() => {
    if (authTypeMeta.value === 'accessCode') {
      // If access code is not enabled, set authType as smsAuthNumber
      if (!isDefaultOrAccessCode) {
        authTypeHelpers.setValue('smsAuthNumber');
      } else if (smsAuthNumberMeta.value) {
        smsAuthNumberHelpers.setValue('');
      }
    } else if (authTypeMeta.value === 'smsAuthNumber') {
      if (accessCodeMeta.value) {
        accessCodeHelpers.setValue('');
      }
    }
  }, [
    accessCodeHelpers,
    accessCodeMeta.value,
    authTypeHelpers,
    authTypeMeta.value,
    isDefaultOrAccessCode,
    smsAuthNumberHelpers,
    smsAuthNumberMeta.value,
  ]);

  return (
    <div className={styles.authenticationFields} role="authenticationFields">
      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: ((accessCodeMeta.touched && accessCodeMeta.error)
            || (smsAuthNumberMeta.touched && smsAuthNumberMeta.error)),
          })}
        >
          <label>
            {intl.formatMessage(messages.authenticationMethodLabel)}
          </label>
          <Dropdown.Wrapper
            kind='small'
            onSelection={handleSelection}
            className={styles.authenticationDropdown}
          >
            <Dropdown.Button>
              {dropdownButtonText}
            </Dropdown.Button>
            <Dropdown.Menu>
              {allowRecipientAccessCode && (
                <Dropdown.MenuItem tag='li' key='accessCode' value='accessCode'>
                  {intl.formatMessage(messages.accessCodeLabel)}
                </Dropdown.MenuItem>
              )}
              {allowRecipientSmsAuth && (
                <Dropdown.MenuItem tag='li' key='SMS' value='smsAuthNumber'>
                  {intl.formatMessage(messages.recipientAuthenticationDropdownSmsAuth)}
                </Dropdown.MenuItem>
              )}
            </Dropdown.Menu>
          </Dropdown.Wrapper>
        </div>
        <div className={styles.fieldRow}>
          {isDefaultOrAccessCode && (
            <div
              className={classNames(
                styles.accessCodeField,
                {
                  [styles.errorFieldContainer]: (accessCodeMeta.touched && accessCodeMeta.error),
                },
              )}
            >
              <label htmlFor={accessCodeFieldName}>
                {intl.formatMessage(messages.recipientCodeLabel)}
                <div className={styles.infoIcon}>
                  <HelpIcon />
                  <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
                    {intl.formatMessage(messages.recipientAccessCodeTooltip)}
                  </TooltipUI>
                </div>
              </label>
              <Field name={accessCodeFieldName}>
                {({ field }: FieldProps) => <TextField
                  id={accessCodeFieldName}
                  autoFocus={props.index === 0}
                  data-qa-ref={`${parent}.fields.accessCode`}
                  className={styles.accessCodeTextbox}
                  {...field} />}
              </Field>
              <div
                className={styles.errorContainer}
                role='accessCode-error'
                data-qa-ref='accessCode-error'
              >
                <ErrorMessage
                  name={accessCodeFieldName}
                  className={styles.error} />
              </div>
            </div>
          )}
          {isSmsAuth && (
            <div
              className={classNames({
                [styles.errorFieldContainer]: smsAuthNumberMeta.touched && smsAuthNumberMeta.error,
              })}
            >
              <label htmlFor={smsAuthFieldName}>
                {intl.formatMessage(messages.recipientPhoneNumberLabel)}
              </label>
              <Field name={smsAuthFieldName}>
                {({ field, form }: FieldProps) => (
                  <ContactInput
                    id={smsAuthFieldName}
                    autoFocus={props.index === 0}
                    className={styles.dropdownField}
                    data-qa-ref={`${parent}.fields.smsAuthNumber`}
                    placeholder='+1 5555555555'
                    contactTypes={['F']}
                    onSelectContact={(contact: types.Contact) => {
                      form.setFieldValue(field.name, contact.value);
                    }}
                    isSmsInput={true}
                    {...field}
                    onChange={(value: string) => form.setFieldValue(field.name, value)} />
                )}
              </Field>
              <div
                className={styles.errorContainer}
                role='smsAuthNumber-error'
                data-qa-ref='smsAuthNumber-error'
              >
                <ErrorMessage
                  name={smsAuthFieldName}
                  className={styles.error} />
              </div>
            </div>
          )}
        </div>
        <span className={styles.removeIcon} data-qa-ref='removeAuthFields'>
          <RemoveIcon onClick={props.onClose} />
        </span>
      </div>
    </div>
  );
};

export default AuthenticationFields;
